import { useRef } from 'react';
import { Card, H2, SGroup, SI, SCheck, Btn, Notice } from '../ui.jsx';
import { C, DISCLAIMER } from '../lib/format.js';

export default function Values({ s, upd, onReset, onExport, onImport, fingerprint, onLock, L }) {
  const { settingsG } = L;
  const fileRef = useRef(null);

  return (
    <div>
      <div style={{ marginBottom: 18, display: 'flex', flexDirection: 'column', gap: 12 }}>
        <Notice tone="good">
          <strong style={{ color: C.bright }}>How to use:</strong> Update figures below from your statements. Changes save
          automatically and sync to your other device.
          <span style={{ color: C.teal }}> Tick events once they've happened.</span>
        </Notice>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: settingsG, gap: 16 }}>

        {/* ── Left column ── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <Card>
            <H2>Current Balances</H2>
            <div style={{ marginBottom: 18 }}>
              <div style={{ fontSize: 12, color: C.lbl, marginBottom: 5 }}>As-of date (shown in header)</div>
              <input type="text" value={s.asOfDate} onChange={e => upd('asOfDate', e.target.value)} placeholder="e.g. Jan 2027" />
            </div>
            <div style={{ marginBottom: 18 }}>
              <div style={{ fontSize: 12, color: C.lbl, marginBottom: 8 }}>
                Simulation start — update each checkup. History uses this to line up forecasts against actuals.
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <SI label="Year" sk="startYear" s={s} upd={upd} />
                <SI label="Month (1–12)" sk="startMonth" min="1" s={s} upd={upd} />
              </div>
            </div>
            <SGroup title="James">
              <SI label="Premium Bonds" sk="jamesPB" prefix="£" s={s} upd={upd} />
              <SI label="S&S ISA" sk="jamesISA" prefix="£" s={s} upd={upd} />
              <SI label="Cash ISA (excluded)" sk="jamesCashISA" prefix="£" s={s} upd={upd} />
              <SI label="Pension" sk="jamesPension" prefix="£" s={s} upd={upd} />
            </SGroup>
            <SGroup title="Joanne">
              <SI label="Premium Bonds" sk="joannePB" prefix="£" s={s} upd={upd} />
              <SI label="S&S ISA" sk="joanneISA" prefix="£" s={s} upd={upd} />
              <SI label="Cash ISA (excluded)" sk="joanneCashISA" prefix="£" s={s} upd={upd} />
              <SI label="Pension" sk="joannePension" prefix="£" s={s} upd={upd} />
            </SGroup>
            <SGroup title="Joint">
              <SI label="Company (gross)" sk="company" prefix="£" s={s} upd={upd} />
              <SI label="House value" sk="houseValue" prefix="£" s={s} upd={upd} />
              <SI label="Mortgage remaining" sk="mortgageRemaining" prefix="£" step="0.01" s={s} upd={upd} />
            </SGroup>
          </Card>

          <Card>
            <H2>One-Time Events</H2>
            <SGroup title="Mortgage">
              <SCheck label="Mortgage already cleared" sk="mortgageCleared" s={s} upd={upd} />
              {!s.mortgageCleared && <>
                <SI label="Clear year" sk="mortgageClearYear" s={s} upd={upd} />
                <SI label="Clear month (1–12)" sk="mortgageClearMonth" min="1" s={s} upd={upd} />
              </>}
            </SGroup>
            <SGroup title="Inheritance">
              <SCheck label="Inheritance still pending (not yet received)" sk="inheritancePending" s={s} upd={upd} />
              {s.inheritancePending && <>
                <SI label="Amount" sk="inheritanceAmount" prefix="£" s={s} upd={upd} />
                <div />
                <SI label="Expected year" sk="inheritanceYear" s={s} upd={upd} />
                <SI label="Expected month (1–12)" sk="inheritanceMonth" min="1" s={s} upd={upd} />
              </>}
            </SGroup>
          </Card>

          <div style={{ textAlign: 'center', paddingBottom: 8 }}>
            <Btn onClick={onReset}>↺ Reset to May 2026 defaults</Btn>
          </div>
        </div>

        {/* ── Right column ── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <Card>
            <H2>Monthly Contributions</H2>
            <SGroup title="ISA (each person per month)">
              <SI label="While mortgage active" sk="isaCurrentEach" prefix="£" s={s} upd={upd} />
              <SI label="After mortgage cleared" sk="isaPostMortgageEach" prefix="£" s={s} upd={upd} />
            </SGroup>
            <SGroup title="Pension (each person per month)">
              <SI label="Monthly total (employer)" sk="pensionEach" prefix="£" s={s} upd={upd} />
            </SGroup>
            <SGroup title="Annual pension top-ups (April)">
              <SI label="Joanne until parity (£/yr)" sk="joanneTopUpAmount" prefix="£" s={s} upd={upd} />
              <SI label="Post-parity each (£/yr)" sk="postParityTopUp" prefix="£" s={s} upd={upd} />
            </SGroup>
          </Card>

          <Card>
            <H2>Growth Rate Assumptions</H2>
            <SGroup title="Annual rates — update if funds change">
              <SI label="S&S ISA (VWRP)" sk="isaGrowthPct" suffix="%" step="0.1" s={s} upd={upd} />
              <SI label="James Pension" sk="jamesPensionGrowthPct" suffix="%" step="0.1" s={s} upd={upd} />
              <SI label="Joanne Pension" sk="joannePensionGrowthPct" suffix="%" step="0.1" s={s} upd={upd} />
              <SI label="Premium Bonds" sk="pbPrizeRatePct" suffix="%" step="0.1" s={s} upd={upd} />
              <SI label="Drawdown growth" sk="drawdownGrowthPct" suffix="%" step="0.1" s={s} upd={upd} />
              <SI label="Inflation / triple-lock min" sk="inflationPct" suffix="%" step="0.1" s={s} upd={upd} />
              <SI label="Cash ISA interest (excluded)" sk="cashISAGrowthPct" suffix="%" step="0.1" s={s} upd={upd} />
            </SGroup>
          </Card>

          <Card>
            <H2>State Pension &amp; Tax</H2>
            <SGroup title="State pension — update after triple-lock rises">
              <SI label="Full new SP (£/yr)" sk="statePension2026" prefix="£" s={s} upd={upd} />
              <div />
              <SI label="James SP start year" sk="jamesStatePensionYear" s={s} upd={upd} />
              <SI label="Joanne SP start year" sk="joanneStatePensionYear" s={s} upd={upd} />
            </SGroup>
            <SGroup title="Tax">
              <SI label="Personal allowance" sk="personalAllowance" prefix="£" s={s} upd={upd} />
            </SGroup>
          </Card>

          {/* ── Backup ── */}
          <Card>
            <H2 sub="Your data is encrypted on this device before it is sent anywhere. If the passphrase is lost, nobody — including us — can recover it. These buttons are your safety net.">
              Backup &amp; Restore
            </H2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <Btn tone="primary" onClick={onExport}>⬇ Export everything</Btn>
              <div style={{ fontSize: 11, color: C.dim, lineHeight: 1.6, marginTop: -4 }}>
                Downloads one readable JSON file containing your current settings and every saved snapshot. On iPhone/iPad
                this opens the share sheet, so you can save it to Files, iCloud Drive or send it to yourself.
              </div>
              <Btn onClick={() => fileRef.current?.click()}>⬆ Import a backup file</Btn>
              <input
                ref={fileRef} type="file" accept="application/json,.json" style={{ display: 'none' }}
                onChange={e => {
                  const f = e.target.files?.[0];
                  if (f) onImport(f);
                  e.target.value = ''; // allow re-importing the same file
                }}
              />
              <div style={{ fontSize: 11, color: C.dim, lineHeight: 1.6, marginTop: -4 }}>
                Restores settings and adds back any snapshots that are missing. Existing snapshots are never overwritten.
              </div>
            </div>
          </Card>

          <Card>
            <H2>This Vault</H2>
            <div style={{ fontSize: 13, color: C.text, lineHeight: 1.7 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <span style={{ color: C.dim, fontSize: 12 }}>Vault fingerprint</span>
                <span style={{ fontFamily: "'DM Mono',monospace", fontSize: 16, color: C.gold, letterSpacing: 1 }}>{fingerprint}</span>
              </div>
              <p style={{ fontSize: 11, color: C.dim, marginTop: 8, lineHeight: 1.6 }}>
                Both devices should show this same code. If Joanne's iPad shows something different, the passphrase was typed
                differently and she is looking at a separate, empty vault.
              </p>
              <div style={{ marginTop: 14 }}>
                <Btn onClick={onLock}>🔒 Lock and forget passphrase on this device</Btn>
              </div>
            </div>
          </Card>

          <Notice>{DISCLAIMER}</Notice>
        </div>
      </div>
    </div>
  );
}
